import type { PolymorphicProps } from "@kobalte/core";
import type { IconProps } from "@storytell/bsky-icons";
import type { IconTypes } from "solid-icons";
import { type Component, type JSX, type ParentComponent, type ValidComponent, children, splitProps } from "solid-js";
import { Dynamic } from "solid-js/web";
import { twMerge } from "tailwind-merge";

// Ignore the fancy polymorphic type, it's there to ensure type inference works
// when you pass components with different props
export const PromptBarButton = <T extends ValidComponent = "button">(
  props: PolymorphicProps<
    T,
    { icon: IconTypes | Component<IconProps>; iconClass?: string; noBorder?: boolean; disabled?: boolean }
  >,
) => {
  const resolved = children(() => props.children);
  const [split, rest] = splitProps(props, ["icon", "noBorder", "iconClass", "disabled"]);

  return (
    <Dynamic
      component={props.as ?? "button"}
      {...rest}
      disabled={split.disabled}
      class={twMerge(
        `group/btn outline-none
        flex items-center justify-center gap-2 rounded-full h-8 cursor-pointer
        focus:ring-2 ring-brand transition-all
        bg-panel
        hover:bg-panel-hover`,
        !split.noBorder && "border border-muted-border dark:border-primary-border hover:border-primary-border",
        split.disabled && "opacity-50 cursor-not-allowed hover:bg-panel",
        props.class,
        resolved.toArray().length === 0 && "w-8",
      )}
    >
      <span
        class={twMerge(
          "group-hover/btn:scale-hover group-active/btn:scale-active transition-transform",
          split.iconClass,
        )}
      >
        <Dynamic component={split.icon as IconTypes} size={18} />
      </span>
      {resolved()}
    </Dynamic>
  );
};
